import { apiFetch } from '../../api';

/**
 * Shared loader for the town code.
 *
 * `GET /codes/` returns the whole table (~420 rows / ~850 KB) and has no search
 * or pagination params, so every code surface needs the same payload. This module
 * is the single place that fetches it: one in-flight promise, one per-id cache.
 *
 * Deliberately in-memory only. Persisting to sessionStorage would push us near
 * the per-origin budget, and serving a stale municipal code from disk is a worse
 * failure than one refetch per session.
 *
 * Note the two response shapes differ: `GET /codes/` omits `violations`, while
 * `GET /codes/{id}` includes it. `primeCodeCache` therefore seeds list rows, but
 * callers that need `violations` must still make the per-id request.
 */

const CODE_CACHE = new Map();
let CODE_LIST_PROMISE = null;

/** Fetch the full code list, sharing a single in-flight request across callers. */
export const loadCodeList = () => {
  if (!CODE_LIST_PROMISE) {
    CODE_LIST_PROMISE = apiFetch('/codes/')
      .then((response) => {
        if (!response.ok) throw new Error('Unable to load code list');
        return response.json();
      })
      .catch((error) => {
        CODE_LIST_PROMISE = null;
        throw error;
      });
  }
  return CODE_LIST_PROMISE;
};

/** Seed the per-id cache from a list response so detail views can paint immediately. */
export const primeCodeCache = (codes) => {
  if (!Array.isArray(codes)) return;
  codes.forEach((code) => {
    if (code?.id == null) return;
    // Never let a list row (no `violations`) overwrite a richer per-id response.
    if (!CODE_CACHE.has(code.id)) CODE_CACHE.set(code.id, code);
  });
};

/** Read a cached code by id. Ids are compared loosely — route params are strings. */
export const getCachedCode = (id) => {
  if (id == null) return null;
  if (CODE_CACHE.has(id)) return CODE_CACHE.get(id);
  const numeric = Number(id);
  if (!Number.isNaN(numeric) && CODE_CACHE.has(numeric)) return CODE_CACHE.get(numeric);
  return CODE_CACHE.get(String(id)) || null;
};

/** Store a single code (typically a per-id response, which carries `violations`). */
export const setCachedCode = (id, code) => {
  if (id == null || !code) return;
  CODE_CACHE.set(id, code);
};

/** Drop everything. Call after a staff create/edit/delete, and in test setup. */
export const invalidateCodeList = () => {
  CODE_CACHE.clear();
  CODE_LIST_PROMISE = null;
};
